import {
  SystemAdminIcon,
  DocumentCenterIcon,
  AdminIcon,
  QuoteIcon,
  MarketingIcon,
  ReportingIcon,
  MyProfileIcon,
  PinpointIcon,
  TrackIcon,
  BookIcon,
} from "assets";
import { User, UserPermissions, UserType } from "domain/users";
import { getParams } from "../common";

export interface OptionLink {
  label: string;
  url: string;
}

interface UserOption {
  icon: React.ReactNode;
  title: string;
  links: OptionLink[];
}

const SAMEDAY_BASE_URL = process.env.REACT_APP_SAMEDAY_BASE_URL;
const QUIKQUOTE_BASE_URL = process.env.REACT_APP_QUIKQUOTE_BASE_URL;
const NDI_BASE_URL = process.env.REACT_APP_NDI_BASE_URL;
const CITYTRAK_BASE_URL = process.env.REACT_APP_CITYTRAK_BASE_URL;
const MSP_ADMIN_BASE_URL = process.env.REACT_APP_MSP_ADMIN_BASE_URL;
const DOCUMENT_CENTER_BASE_URL = process.env.REACT_APP_DOCUMENT_CENTER_BASE_URL;
const TRACKING_APP_URL = process.env.REACT_APP_TRACKING_APP_BASE_URL;

const getTile = (icon: React.ReactNode, title: string): UserOption => ({
  icon,
  title,
  links: []
});

const getBookTile = () => getTile(<BookIcon />, "Book");
const getQuoteTile = () => getTile(<QuoteIcon />, "Quote");
const getTrackTile = () => getTile(<TrackIcon />, "Track");
const getReportingTile = () => getTile(<ReportingIcon />, "Reporting");
const getMyProfileTile = () => getTile(<MyProfileIcon />, "My profile");
const getPinpointTile = () => getTile(<PinpointIcon />, "Pinpoint");
const getAdminTile = () => getTile(<AdminIcon />, "Admin");
const getSystemAdminTile = () => getTile(<SystemAdminIcon />, "System Admin");
const getMarketingTile = () => getTile(<MarketingIcon />, "Marketing");
const getDocumentCenterTile = () =>
  getTile(<DocumentCenterIcon />, "Document Centre platform");

const getAllLinks = (
  { userID, email }: User,
  accountID: number
) => {
  const params = getParams(userID, accountID);

  return {
    samedayCourier: {
      label: "SameDay Courier",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/Booking/Booking.aspx?${params}`,
    },
    internationalCourier: {
      label: "International",
      url: `${NDI_BASE_URL}/book?${params}`,
    },
    overnight: {
      label: "Next Day",
      url: `${NDI_BASE_URL}/book?${params}`,
    },
    manualCollection: {
      label: "SameDay Collection",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/Booking/Newcollection.aspx?${params}`,
    },
    manualBooking: {
      label: "SameDay Delivery",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/Booking/NewBooking.aspx?${params}`,
    },
    bookingQuote: {
      label: "SameDay Courier",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/Booking/Booking.aspx?Isq=Y&${params}`,
    },
    systemAdmin: {
      label: "Systems admin",
      url: `${SAMEDAY_BASE_URL}/SameDay/Admin/CCGManagementViewAmend.aspx?${params}`,
    },
    marketing: {
      label: "Marketing",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/Admin/ServiceCentreDetails.aspx?${params}`,
    },
    report: {
      label: "SameDay Courier",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/Reports/Reports.aspx?${params}`,
    },
    myProfile: {
      label: "My account",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/MyProfile/SenderDetails.aspx?${params}`,
    },
    myAddress: {
      label: "Manage my address book",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/MyProfile/ViewAmendAddress.aspx?${params}`,
    },
    admin: {
      label: "SameDay online",
      url: `${SAMEDAY_BASE_URL}/SameDay/Admin/ViewAmendUser.aspx?${params}`,
    },
    pinpoint: {
      label: "Dashboard",
      url: `${CITYTRAK_BASE_URL}/webapp/client/index.html#/dashboard?${params}`,
    },
    internationalCourierReport: {
      label: "International",
      url: `${NDI_BASE_URL}/order_history?${params}`,
    },
    overnightParcelsReport: {
      label: "Next Day",
      url: `${NDI_BASE_URL}/order_history?${params}`,
    },
    nonAccountQuote: {
      label: "Book",
      url: `${QUIKQUOTE_BASE_URL}/QuoteAndBooking/Quote.aspx?${params}`,
    },
    nonAccountMyProfile: {
      label: "My Details",
      url: `${QUIKQUOTE_BASE_URL}/MyProfile/MyProfileDetails.aspx?${params}`,
    },
    nonAccountMyBookings: {
      label: "My Bookings",
      url: `${QUIKQUOTE_BASE_URL}/MyProfile/ViewMyBookings.aspx?${params}`,
    },
    track: {
      label: "SameDay Courier",
      url: `${TRACKING_APP_URL}`,
    },
    msp: {
      label: "BBC - Find user",
      url: `${SAMEDAY_BASE_URL}/SameDay/Admin/BBCLoginUser.aspx?${params}`,
    },
    mspAdmin: {
      label: "BBC - Admin",
      url: `${MSP_ADMIN_BASE_URL}?EmailAddress=${email}`,
    },
    documentCenter: {
      label: "Document centre",
      url: `${DOCUMENT_CENTER_BASE_URL}/admin?${params}`,
    },
    overnightParcelsQuote: {
      label: "Next Day",
      url: `${NDI_BASE_URL}/book?${params}`,
    },
    internationalCourierQuote: {
      label: "International",
      url: `${NDI_BASE_URL}/book?${params}`,
    },
    overnightTrack: {
      label: "Next Day",
      url: `${TRACKING_APP_URL}`,
    },
    internationalTrack: {
      label: "International",
      url: `${TRACKING_APP_URL}`,
    },
    myBookings: {
      label: "Manage my booking details",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/MyProfile/BookingDefaults.aspx?${params}`,
    },
    requestNewAccount: {
      label: "Request new account access",
      url: `${SAMEDAY_BASE_URL}/SameDayRedesign/MyProfile/RequestForNewAccountAccess.aspx?${params}`,
    },
  };
};

const getQuikQuoteUserOptions = (
  user: User,
  accountID: number,
  permissions: UserPermissions
): UserOption[] => {
  const {
    nonAccountQuote,
    manualBooking,
    manualCollection,
    nonAccountMyBookings,
    nonAccountMyProfile,
  } = getAllLinks(user, accountID);

  const bookTile = getBookTile();

  if (!permissions.canDoManualBooking) {
    bookTile.links = [nonAccountQuote];
  } else {
    bookTile.links = [manualBooking, manualCollection];
  }

  const myProfileTile = getMyProfileTile();

  myProfileTile.links = [nonAccountMyBookings, nonAccountMyProfile];

  return [bookTile, myProfileTile];
};

const getSamedayUserOptions = (
  user: User,
  accountID: number,
  {
    canDoAdmin,
    canDoBooking,
    canDoDocumentCenter,
    canDoMSP,
    canDoMSPAdmin,
    canDoMarketing,
    canDoMyProfile,
    canDoPinpoint,
    canDoQuotes,
    canDoReports,
    canDoSystemAdmin,
    canDoTracking,
    canDoManualBooking,
  }: UserPermissions
): UserOption[] => {
  const options: UserOption[] = [];

  const {
    samedayCourier,
    overnight,
    internationalCourier,
    manualBooking,
    manualCollection,
    admin,
    bookingQuote,
    documentCenter,
    internationalCourierQuote,
    internationalCourierReport,
    internationalTrack,
    marketing,
    msp,
    mspAdmin,
    myAddress,
    myBookings,
    myProfile,
    overnightParcelsQuote,
    overnightParcelsReport,
    overnightTrack,
    pinpoint,
    report,
    requestNewAccount,
    systemAdmin,
    track,
  } = getAllLinks(user, accountID);

  if (canDoBooking) {
    const bookTile = getBookTile();

    if (!canDoManualBooking) {
      bookTile.links = [samedayCourier, overnight, internationalCourier];
    } else {
      bookTile.links = [manualBooking, manualCollection];
    }

    options.push(bookTile);
  }

  if (canDoQuotes) {
    const quoteTile = getQuoteTile();

    quoteTile.links = [
      bookingQuote,
      overnightParcelsQuote,
      internationalCourierQuote,
    ];

    options.push(quoteTile);
  }

  if (canDoTracking) {
    const trackingTile = getTrackTile();

    trackingTile.links = [track];

    if (!canDoManualBooking) {
      trackingTile.links.push(overnightTrack, internationalTrack);
    }

    options.push(trackingTile);
  }

  if (canDoReports) {
    const reportsTile = getReportingTile();

    reportsTile.links = [report];

    if (!canDoManualBooking) {
      reportsTile.links.push(
        overnightParcelsReport,
        internationalCourierReport
      );
    }

    options.push(reportsTile);
  }

  if (canDoMyProfile) {
    const myProfileTile = getMyProfileTile();

    myProfileTile.links = [myProfile, myAddress, myBookings, requestNewAccount];

    options.push(myProfileTile);
  }

  if (canDoPinpoint) {
    const pinpointTile = getPinpointTile();

    pinpointTile.links = [pinpoint];

    options.push(pinpointTile);
  }

  if (canDoAdmin) {
    const adminTile = getAdminTile();

    adminTile.links = [admin];

    if (canDoMSP) {
      adminTile.links.push(msp);
    }

    if (canDoMSPAdmin) {
      adminTile.links.push(mspAdmin);
    }

    options.push(adminTile);
  }

  if (canDoSystemAdmin) {
    const sysAdminTile = getSystemAdminTile();

    sysAdminTile.links = [systemAdmin];

    options.push(sysAdminTile);
  }

  if (canDoMarketing) {
    const marketingTile = getMarketingTile();

    marketingTile.links = [marketing];

    options.push(marketingTile);
  }

  if (canDoDocumentCenter) {
    const documentCenterTile = getDocumentCenterTile();

    documentCenterTile.links = [documentCenter];

    options.push(documentCenterTile);
  }

  return options;
};

export const getOptions = (
  user: User,
  accountID: number,
  permissions: UserPermissions
): UserOption[] => {
  if (user.userType === UserType.QuikQuoteUser) {
    return getQuikQuoteUserOptions(user, accountID, permissions);
  }

  return getSamedayUserOptions(user, accountID, permissions);
};
